/**
 * Ordre « dans le service » pour les services qui franchissent minuit (lot C).
 *
 * Une heure appartient à la portion après minuit (+1440) uniquement si elle tombe
 * avant la fin d'un créneau qui franchit minuit ET dans aucun créneau ordinaire.
 * Sans créneau de nuit, toutes les fonctions renvoient exactement le résultat
 * historique (minutes depuis minuit / tri alphabétique HH:mm).
 */
export interface SlotBounds {
  start: string;
  end: string;
}

const toMin = (t: string): number => {
  const [h, m] = t.substring(0, 5).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export function isOvernightSlot(s: SlotBounds): boolean {
  return toMin(s.end) < toMin(s.start);
}

export function hasOvernight(slots: SlotBounds[] | null | undefined): boolean {
  return (slots || []).some(isOvernightSlot);
}

/** Minutes de service d'une heure (identique à minutes depuis minuit sans créneau de nuit). */
export function serviceSortMinutes(time: string, slots: SlotBounds[] | null | undefined): number {
  const t = toMin(time);
  const list = slots || [];
  if (!hasOvernight(list)) return t;
  const inPlain = list.some(s => !isOvernightSlot(s) && t >= toMin(s.start) && t < toMin(s.end));
  if (inPlain) return t;
  const afterMidnight = list.some(s => isOvernightSlot(s) && t < toMin(s.start) && t < toMin(s.end));
  return afterMidnight ? t + 1440 : t;
}

/**
 * Comparateur d'heures. Sans créneau de nuit : localeCompare (comportement actuel,
 * y compris pour les chaînes HH:mm:ss). Avec créneau de nuit : ordre du service.
 */
export function makeServiceTimeCompare(slots: SlotBounds[] | null | undefined) {
  if (!hasOvernight(slots)) {
    return (a: string, b: string) => a.localeCompare(b);
  }
  return (a: string, b: string) => {
    const d = serviceSortMinutes(a, slots) - serviceSortMinutes(b, slots);
    return d !== 0 ? d : a.localeCompare(b);
  };
}

/** Appartenance d'une heure à un créneau, y compris un créneau qui franchit minuit. */
export function isTimeWithinSlot(time: string, slot: SlotBounds): boolean {
  const t = toMin(time), s = toMin(slot.start), e = toMin(slot.end);
  if (e >= s) return t >= s && t < e;
  return t >= s || t < e;
}

/**
 * Chevauchement réservation ↔ créneau (mode TIME). Identique à l'historique pour
 * un créneau ordinaire ; pour un créneau de nuit, une heure de la portion après
 * minuit (avant la fin horloge du créneau) est décalée de +1440.
 */
export function reservationOverlapsSlot(
  reservationTime: string,
  durationMinutes: number,
  slotStart: string,
  slotEnd: string,
): boolean {
  const ss = toMin(slotStart);
  let se = toMin(slotEnd);
  let rs = toMin(reservationTime);
  if (se < ss) {
    if (rs < se) rs += 1440;
    se += 1440;
  }
  return rs < se && rs + durationMinutes > ss;
}

/** Heure de fin la plus tardive autorisée pour un service qui finit le lendemain. */
export const OVERNIGHT_END_MAX = "06:00";

/**
 * Validation de saisie d'un créneau. `null` = valide.
 * Fin > début : créneau ordinaire (inchangé). Fin < début : finit le lendemain, au plus tard 06:00.
 */
export function validateSlotTimes(start: string, end: string): string | null {
  if (!start || !end) return null;
  const s = start.substring(0, 5);
  const e = end.substring(0, 5);
  if (e === s) return "L'heure de fin doit être différente de l'heure de début.";
  if (e < s && e > OVERNIGHT_END_MAX) {
    return `Un service qui finit le lendemain doit se terminer au plus tard à ${OVERNIGHT_END_MAX}.`;
  }
  return null;
}
